import * as React from "react";
import { Scissors, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { GoalItemsPanel } from "../GoalItemsPanel.js";
import { LoadError } from "@/components/LoadError.js";
import type { GoalDetail } from "../types.js";

/**
 * DeclutterGoalView — detail body for a "declutter" goal.
 *
 * Renders the shared items panel plus the declutter-suggestions section. In a
 * later PR (Workstream D.1) the suggestions/decision rows converge with the
 * standalone Declutter tool's components; for this pure-decomposition PR the
 * behavior is unchanged from the monolith.
 */
export function DeclutterGoalView({
  goal,
  goalId,
  isOwner,
  onSetGoal,
  refetchGoal,
}: {
  goal: GoalDetail;
  goalId: string;
  isOwner: boolean;
  onSetGoal: (goal: GoalDetail) => void;
  refetchGoal: () => Promise<void> | void;
}) {
  const [showLinkItems, setShowLinkItems] = React.useState(false);

  return (
    <>
      <GoalItemsPanel
        goal={goal}
        goalId={goalId}
        isOwner={isOwner}
        onSetGoal={onSetGoal}
        onItemsChanged={refetchGoal}
        showLinkItems={showLinkItems}
        setShowLinkItems={setShowLinkItems}
      />

      {goal.status === "active" && goal.items.length > 0 && (
        <DeclutterSuggestionsSection goalId={goalId} />
      )}
    </>
  );
}

// =============================================================================
// DECLUTTER SUGGESTIONS SECTION — Declutter as a solution type
// =============================================================================

interface DeclutterCandidate {
  itemId: string;
  itemName: string;
  spaceName: string | null;
  daysSinceLastUsed: number | null;
  reason: "linked_unused" | "space_clutter";
}

interface DeclutterResponse {
  goalTitle: string;
  suggestion: string;
  candidates: DeclutterCandidate[];
  totalCandidates: number;
}

function DeclutterSuggestionsSection({ goalId }: { goalId: string }) {
  const [data, setData] = React.useState<DeclutterResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const fetchSuggestions = React.useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await apiFetch<DeclutterResponse>(
        `/api/organizing-goals/${goalId}/declutter-suggestions`
      );
      setData(result);
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [goalId]);

  // Fetch on first expand (not while an error is showing — retry is manual then)
  React.useEffect(() => {
    if (expanded && !data && !loading && !error) {
      fetchSuggestions();
    }
  }, [expanded, data, loading, error, fetchSuggestions]);

  return (
    <div className="bg-white rounded-lg border border-clay-border p-5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full"
      >
        <h2 className="font-semibold text-[1.0625rem] text-ink flex items-center gap-2">
          <Scissors className="w-4 h-4 text-sage-deep" />Declutter opportunity
        </h2>
        <span className="text-[0.8125rem] text-faint">{expanded ? "Hide" : "Check"}</span>
      </button>

      {expanded && (
        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-sage-deep" />
            </div>
          ) : data && data.candidates.length > 0 ? (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-sage-wash border border-clay-border">
                <p className="text-sm text-sage-deep">{data.suggestion}</p>
              </div>
              <div className="space-y-1">
                {data.candidates.slice(0, 10).map((candidate) => (
                  <div
                    key={candidate.itemId}
                    className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-amethyst-soft"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-pill ${candidate.reason === "linked_unused" ? "bg-warning" : "bg-clay-border"}`} />
                      <span className="text-sm text-ink">{candidate.itemName}</span>
                      {candidate.spaceName && (
                        <span className="text-[0.8125rem] text-faint">in {candidate.spaceName}</span>
                      )}
                    </div>
                    <span className="text-[0.8125rem] text-faint tabular-nums">
                      {candidate.daysSinceLastUsed !== null
                        ? `${candidate.daysSinceLastUsed}d unused`
                        : "Never used"}
                    </span>
                  </div>
                ))}
              </div>
              {data.totalCandidates > 10 && (
                <p className="text-[0.8125rem] text-faint text-center tabular-nums">
                  + {data.totalCandidates - 10} more items
                </p>
              )}
              <a
                href="/organize/declutter"
                className="block text-center text-sm text-sage-deep font-medium hover:text-sage-deep-hover mt-2 py-2"
              >
                Go to declutter tool →
              </a>
            </div>
          ) : error ? (
            <LoadError variant="inline" message="Couldn't load declutter suggestions." onRetry={fetchSuggestions} retrying={loading} />
          ) : data ? (
            <p className="text-sm text-body text-center py-3">
              {data.suggestion}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
